import { QBankStructureLevelCategory } from '@exam-management/graphql';
import { TableBody } from '@shared/components';
import { useCallback, useState } from 'react';

import {
  ChapterTableRow,
  SubjectTableRow,
  type QBankStructureSettingSectionTableData,
} from '../../components';
import { useQConfigGeneratorCreationForm } from '../../providers';

import { computeExpansionState, getChildrenQuestionCount } from './utils';

interface QBankStructureTableBodyProps {
  rows: QBankStructureSettingSectionTableData[];
  handleDeleteMenu: (id: string) => void;
}

export default function QBankStructureTableBody(props: Readonly<QBankStructureTableBodyProps>) {
  const { rows, handleDeleteMenu } = props;

  const allParentIds = new Set(rows.flatMap((row) => row.parentIds));
  const [openIds, setOpenIds] = useState<Set<string>>(allParentIds);
  const formContext = useQConfigGeneratorCreationForm();
  const { watch } = formContext;
  const chapterQuestionDistribution = watch('chapterQuestionDistribution');
  const toggleItem = useCallback((id: string) => {
    setOpenIds((prev) => {
      const updatedSet = new Set(prev);
      if (updatedSet.has(id)) {
        updatedSet.delete(id);
      } else {
        updatedSet.add(id);
      }
      return updatedSet;
    });
  }, []);

  return (
    <TableBody>
      {rows.map((row) => {
        const { id, parentIds, level } = row;
        const { isParentExpanded, isExpanded } = computeExpansionState(id, openIds, parentIds);

        if (!isParentExpanded) {
          return null;
        }

        switch (level) {
          case QBankStructureLevelCategory.Subject: {
            const { childrenQuestionCount } = getChildrenQuestionCount({
              id,
              rows,
              chapterQuestionDistribution,
            });
            return (
              <SubjectTableRow
                key={id}
                data={row}
                expanded={isExpanded}
                onExpand={toggleItem}
                childrenQuestionCount={childrenQuestionCount}
                handleDeleteMenu={handleDeleteMenu}
              />
            );
          }
          case QBankStructureLevelCategory.Chapter:
            return (
              <ChapterTableRow
                key={id}
                data={row}
                expanded={isExpanded}
                onExpand={toggleItem}
                handleDeleteMenu={handleDeleteMenu}
              />
            );
          default:
            return null;
        }
      })}
    </TableBody>
  );
}
